import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { interpolate } from "../interpolate.js";
import { DEFAULT_LOCALE, detectBrowserLocale, normalizeLocaleCode, resolveInitialLocale, } from "../locale.js";
import { mergeMessages } from "../merge.js";
import { I18N_SOURCE_LOCALE, sourceMessages } from "../messages.js";
import { readCachedCatalog, readStoredLocaleChoice, writeCachedCatalog, writeStoredLocaleChoice, } from "./catalog-cache.js";
const POLL_INTERVAL_MS = 5_000;
const I18nContext = createContext(null);
export function I18nProvider({ children, client, userPreferredLocale, onPersistPreference, onLocaleReady, onLocaleFailed, }) {
    const navigatorLanguages = useMemo(() => {
        if (typeof navigator === "undefined")
            return [];
        if (navigator.languages && navigator.languages.length > 0)
            return navigator.languages;
        return navigator.language ? [navigator.language] : [];
    }, []);
    const initialResolved = useMemo(() => resolveInitialLocale(userPreferredLocale, readStoredLocaleChoice(), navigatorLanguages), 
    // Résolution une seule fois au montage : les changements ultérieurs
    // passent par setLocale ou par l'effet de préférence compte ci-dessous
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const [locale, setLocaleState] = useState(initialResolved.locale);
    const [detectedFrom, setDetectedFrom] = useState(initialResolved.detectedFrom);
    const [remoteMessages, setRemoteMessages] = useState({});
    const [remoteLocale, setRemoteLocale] = useState(null);
    const [localeVersion, setLocaleVersion] = useState(0);
    const [localeStatus, setLocaleStatus] = useState(null);
    const [isLoadingRemote, setIsLoadingRemote] = useState(false);
    const [locales, setLocales] = useState([]);
    const [pendingLocale, setPendingLocale] = useState(null);
    const hasExplicitChoiceRef = useRef(false);
    const warnedMissingKeysRef = useRef(new Set());
    const browserLocale = useMemo(() => detectBrowserLocale(navigatorLanguages), [navigatorLanguages]);
    const applyCatalog = useCallback((loc, version, messages) => {
        setRemoteMessages(messages);
        setRemoteLocale(loc);
        setLocaleVersion(version);
    }, []);
    // Charge le catalogue de la locale demandée : cache localStorage d'abord,
    // puis revalidation par /status et URL versionnée immuable si nécessaire.
    const loadCatalog = useCallback(async (loc) => {
        if (loc === I18N_SOURCE_LOCALE) {
            setRemoteMessages({});
            setRemoteLocale(null);
            setLocaleVersion(0);
            setLocaleStatus("ready");
            return "ready";
        }
        const cached = readCachedCatalog(loc);
        if (cached && cached.version > 0) {
            applyCatalog(loc, cached.version, cached.messages);
        }
        setIsLoadingRemote(true);
        try {
            const status = await client.fetchStatus(loc);
            if (!status) {
                setLocaleStatus(null);
                if (!cached) {
                    setRemoteMessages({});
                    setRemoteLocale(null);
                    setLocaleVersion(0);
                }
                return null;
            }
            setLocaleStatus(status.status);
            // Le catalogue est servi dès qu'il existe, quel que soit l'état de la
            // génération en cours : une retraduction échouée ou en cours laisse
            // l'ancien catalogue intact côté serveur. Le conditionner à « ready »
            // renvoyait en anglais tout visiteur sans cache local.
            if (status.version > 0) {
                if (cached && cached.version === status.version) {
                    return status.status;
                }
                const catalog = await client.fetchCatalog(loc, status.version);
                if (catalog) {
                    writeCachedCatalog(catalog);
                    applyCatalog(loc, catalog.version, catalog.messages);
                }
            }
            return status.status;
        }
        catch {
            if (typeof console !== "undefined") {
                console.warn(`[i18n] Failed to load remote locale "${loc}"`);
            }
            setLocaleStatus(cached ? "ready" : null);
            return null;
        }
        finally {
            setIsLoadingRemote(false);
        }
    }, [client, applyCatalog]);
    const refreshLocales = useCallback(async () => {
        try {
            const response = await client.fetchLocales();
            setLocales(response.locales);
        }
        catch {
            // non bloquant : le sélecteur affichera au minimum la locale source
        }
    }, [client]);
    // Liste des locales au montage
    useEffect(() => {
        void refreshLocales();
    }, [refreshLocales]);
    // Catalogue de la locale active
    useEffect(() => {
        void loadCatalog(locale);
    }, [locale, loadCatalog]);
    // Préférence compte : adoptée à la connexion si l'utilisateur n'a pas fait
    // de choix explicite dans la session
    useEffect(() => {
        if (!userPreferredLocale || hasExplicitChoiceRef.current)
            return;
        const normalized = normalizeLocaleCode(userPreferredLocale);
        if (normalized && normalized !== locale) {
            setLocaleState(normalized);
            setDetectedFrom("account");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPreferredLocale]);
    // Polling pendant une traduction : locale demandée explicitement, sinon
    // locale active en attente. Bascule automatique quand la locale devient ready.
    const watchedLocale = pendingLocale ??
        (locale !== I18N_SOURCE_LOCALE &&
            (localeStatus === "waiting_trad" || localeStatus === "translating")
            ? locale
            : null);
    useEffect(() => {
        if (!watchedLocale)
            return;
        const id = setInterval(() => {
            void (async () => {
                try {
                    const status = await client.fetchStatus(watchedLocale);
                    if (!status)
                        return;
                    if (watchedLocale === locale) {
                        setLocaleStatus(status.status);
                    }
                    if (status.status === "ready") {
                        if (status.version > 0) {
                            const catalog = await client.fetchCatalog(watchedLocale, status.version);
                            if (catalog)
                                writeCachedCatalog(catalog);
                        }
                        if (pendingLocale === watchedLocale) {
                            setPendingLocale(null);
                            setLocaleState(watchedLocale);
                            setDetectedFrom("storage");
                            writeStoredLocaleChoice(watchedLocale);
                        }
                        else {
                            void loadCatalog(locale);
                        }
                        void refreshLocales();
                        onLocaleReady?.(watchedLocale);
                    }
                    else if (status.status === "translation_failed") {
                        const wasRequested = pendingLocale === watchedLocale;
                        if (wasRequested)
                            setPendingLocale(null);
                        void refreshLocales();
                        // Une retraduction échouée d'une langue déjà affichée ne change rien
                        // pour la personne : son catalogue reste valable. L'alerte ne
                        // concerne qu'une demande explicite ou une langue sans catalogue.
                        if (watchedLocale === locale && status.version > 0)
                            void loadCatalog(locale);
                        if (wasRequested || status.version === 0)
                            onLocaleFailed?.(watchedLocale);
                    }
                }
                catch {
                    // non bloquant : nouvel essai au tick suivant
                }
            })();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [
        watchedLocale,
        locale,
        pendingLocale,
        client,
        loadCatalog,
        refreshLocales,
        onLocaleReady,
        onLocaleFailed,
    ]);
    const setLocale = useCallback((next) => {
        const normalized = normalizeLocaleCode(next);
        if (!normalized)
            return;
        hasExplicitChoiceRef.current = true;
        setPendingLocale(null);
        setLocaleState(normalized);
        setDetectedFrom("storage");
        writeStoredLocaleChoice(normalized);
        void Promise.resolve(onPersistPreference?.(normalized)).catch(() => {
            // non bloquant : la préférence locale reste appliquée
        });
    }, [onPersistPreference]);
    // Demande explicite de traduction d'une locale (consentement utilisateur).
    // L'interface reste dans la langue courante ; bascule automatique au ready.
    const requestLocale = useCallback(async (target) => {
        const normalized = normalizeLocaleCode(target);
        if (!normalized || normalized === I18N_SOURCE_LOCALE)
            return false;
        try {
            const result = await client.requestLocale(normalized);
            if (!result.ok)
                return false;
            hasExplicitChoiceRef.current = true;
            setPendingLocale(normalized);
            void refreshLocales();
            return true;
        }
        catch {
            return false;
        }
    }, [client, refreshLocales]);
    const mergedMessages = useMemo(() => mergeMessages(sourceMessages, remoteLocale === locale ? remoteMessages : {}), [locale, remoteLocale, remoteMessages]);
    // Reflète ce qui est réellement rendu par mergedMessages ci-dessus : tant
    // que le catalogue de `locale` n'a pas été appliqué, l'affichage retombe sur
    // la source, donc l'indicateur de langue doit en faire autant.
    const renderedLocale = locale === I18N_SOURCE_LOCALE || remoteLocale === locale ? locale : I18N_SOURCE_LOCALE;
    const t = useCallback((key, values) => {
        const resolved = mergedMessages[key];
        if (resolved === undefined) {
            if (typeof process !== "undefined" &&
                process.env.NODE_ENV !== "production" &&
                !warnedMissingKeysRef.current.has(key)) {
                warnedMissingKeysRef.current.add(key);
                console.warn(`[i18n] Missing message key: "${key}"`);
            }
            return key;
        }
        return interpolate(resolved, values);
    }, [mergedMessages]);
    // Reflète la locale effectivement rendue sur le document (pas la locale
    // demandée : tant qu'elle n'est pas chargée, le contenu reste en anglais).
    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.lang = renderedLocale;
        }
    }, [renderedLocale]);
    const value = useMemo(() => ({
        locale,
        renderedLocale,
        sourceLocale: I18N_SOURCE_LOCALE,
        browserLocale,
        detectedFrom,
        locales,
        localeStatus,
        localeVersion,
        isLoadingRemote,
        pendingLocale,
        t,
        setLocale,
        requestLocale,
        refreshLocales,
    }), [
        locale,
        renderedLocale,
        browserLocale,
        detectedFrom,
        locales,
        localeStatus,
        localeVersion,
        isLoadingRemote,
        pendingLocale,
        t,
        setLocale,
        requestLocale,
        refreshLocales,
    ]);
    return _jsx(I18nContext.Provider, { value: value, children: children });
}
// Contexte de secours hors provider : anglais pur, actions inertes.
// Divergence volontaire vis-à-vis d'ODB (qui lève une erreur) : les tests
// ExpertLoop rendent les composants sans wrapper, et l'anglais est le
// fallback permanent — un composant non enveloppé reste donc fonctionnel.
const FALLBACK_CONTEXT = {
    locale: I18N_SOURCE_LOCALE,
    renderedLocale: I18N_SOURCE_LOCALE,
    sourceLocale: I18N_SOURCE_LOCALE,
    browserLocale: null,
    detectedFrom: "default",
    locales: [],
    localeStatus: "ready",
    localeVersion: 0,
    isLoadingRemote: false,
    pendingLocale: null,
    t: (key, values) => {
        const resolved = sourceMessages[key];
        return resolved === undefined ? key : interpolate(resolved, values);
    },
    setLocale: () => { },
    requestLocale: async () => false,
    refreshLocales: async () => { },
};
export function useI18n() {
    const ctx = useContext(I18nContext);
    return ctx ?? FALLBACK_CONTEXT;
}
export { DEFAULT_LOCALE };
//# sourceMappingURL=I18nProvider.js.map